import React, { useState, useEffect } from 'react';
import {
  FormControl,
  FormLabel,
  Button,
  Text,
  useToast,
} from '@chakra-ui/react';
import GlobalModal from 'components/GlobalModal';
import InterfaceSelect from 'components/select';

const UGX_ASSET_MASTER_API_URL = process.env.REACT_APP_UGX_ASSET_MASTER_API_URL;

function AssetParamDialog({ open, onClose, updateSlaveParams }) {
  const [typeData, setTypeData] = useState([]);
  const [selectedType, setSelectedType] = useState('');

  const [makeData, setMakeData] = useState([]);
  const [selectedMake, setSelectedMake] = useState('');

  const [modelData, setModelData] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');

  const [error, setError] = useState('');
  const toast = useToast();

  const CREDENTIALS = {
    email: process.env.REACT_APP_ASSET_MASTER_USER_EMAIL,
    password: process.env.REACT_APP_ASSET_MASTER_USER_PASSWORD,
  };

  const getAccessToken = () => localStorage.getItem('ugxAccess');
  const getRefreshToken = () => localStorage.getItem('ugxRefresh');

  const getDataType = (type) => {
    const typeMap = {
      1: '16bit_int',
      2: '32bit_uint',
      3: '32bit_float',
      14: '16bit_uint',
      18: '32bit_int',
      26: '16bit_float',
      42: 'Str',
      43: 'bool',
    };
    return typeMap[type] || '';
  };

  // Register data type mapping based on subtype field
  const getRegDataType = (subtype) => {
    const subtypeMap = {
      0: 'DCBA',
      1: 'ABCD',
      2: 'CDAB',
      3: 'BADC',
      4: 'bool',
    };
    return subtypeMap[subtype] || '';
  };

  const getWriteFn = (writeFnCode) => {
    const fnMap = {
      '-1': 'Read Only',
      5: 'Write Coil',
      6: 'Write Register',
      10: 'Multiple Write',
    };
    if (typeof writeFnCode === 'string' && writeFnCode === '[]')
      return 'Read Only';
    return fnMap[writeFnCode] || 'Read Only';
  };

  const getAccess = (action) => {
    const accessMap = {
      0: 'Read Only',
      1: 'Read Write',
      2: 'trigger',
    };
    return accessMap[action] || '';
  };

  const getRegCount = (type) => {
    const regCountMap = {
      1: 1, // 16bit_int
      2: 2, // 32bit_uint
      3: 2, // 32bit_float
      14: 1, // 16bit_uint
      18: 2, // 32bit_int
      26: 1, // 16bit_float
      42: 1, // Str
      43: 1, // bool
    };
    return regCountMap[type] || 1;
  };

  const getModelParameters = async (id) => {
    try {
      const res = await fetch(
        `${UGX_ASSET_MASTER_API_URL}/assets/model-params/${id}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        },
      );

      if (!res.ok) throw new Error('Something went wrong. Try again later.');
      const data = await res.json();

      if (!data.parameters || data.parameters.length === 0) {
        toast({
          title: 'No Parameters Found',
          description: 'The selected model has no parameters to add.',
          status: 'warning',
          duration: 3000,
          position: 'top',
          isClosable: true,
        });
        return;
      }

      const validParams = [];
      const invalidParams = [];
      // const warnings = [];

      data.parameters.forEach((param, index) => {
        const issues = [];

        // Check for required fields
        if (param.address === undefined || param.address === null) {
          issues.push('missing address');
        }
        if (param.fn_code === undefined || param.fn_code === null) {
          issues.push('missing function code');
        }
        if (param.type === undefined || param.type === null) {
          issues.push('missing type');
        }

        // Check for unsupported values
        if (param.type !== undefined && !getDataType(param.type)) {
          issues.push('unsupported data type');
        }
        if (param.subtype !== undefined && !getRegDataType(param.subtype)) {
          issues.push('unsupported register data type');
        }
        if (param.action !== undefined && getAccess(param.action) === '') {
          issues.push('unsupported access type');
        }

        if (issues.length > 0) {
          invalidParams.push({
            name: param.name || `Parameter ${index + 1}`,
            issues: issues,
          });
        } else {
          // Transform valid parameters
          validParams.push({
            id: Date.now() + index,
            name: param.name || '',
            fnCode: param.fn_code || 3,
            address: param.address || 0,
            type: getDataType(param.type),
            factor: param.factor || 1.0,
            regCount: getRegCount(param.type),
            regDataType: getRegDataType(param.subtype),
            offset: 0,
            writeFn: getWriteFn(param.write_fn_code),
            access: getAccess(param.action),
          });
        }
      });

      // Show warnings for invalid parameters
      if (invalidParams.length > 0) {
        const invalidList = invalidParams
          .map((p) => `${p.name}: ${p.issues.join(', ')}`)
          .join('; ');
        toast({
          title: 'Parameter Validation Issues',
          description: `${invalidParams.length} parameter(s) skipped due to issues: ${invalidList}`,
          status: 'warning',
          duration: 5000,
          position: 'top',
          isClosable: true,
        });
      }

      if (validParams.length === 0) {
        toast({
          title: 'No Valid Parameters',
          description: 'No parameters could be added due to validation issues.',
          status: 'error',
          duration: 3000,
          position: 'top',
          isClosable: true,
        });
        return;
      }

      // Show success message for valid parameters
      if (validParams.length > 0) {
        toast({
          title: 'Parameters Added Successfully',
          description: `${validParams.length} valid parameter(s) added to the slave.`,
          status: 'success',
          duration: 3000,
          position: 'top',
          isClosable: true,
        });

        updateSlaveParams(validParams);
      }

      // Reset form state
      setSelectedType('');
      setSelectedMake('');
      setSelectedModel('');
      setMakeData([]);
      setModelData([]);

      onClose();
    } catch (err) {
      setError(err.message);
      toast({
        title: 'Error Loading Parameters',
        description: err.message,
        status: 'error',
        duration: 3000,
        position: 'top',
        isClosable: true,
      });
    }
  };

  const login = async () => {
    const res = await fetch(`${UGX_ASSET_MASTER_API_URL}/token/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(CREDENTIALS),
    });

    if (!res.ok) throw new Error('Login failed');

    const data = await res.json();
    localStorage.setItem('ugxAccess', data.access);
    localStorage.setItem('ugxRefresh', data.refresh);
    return data.access;
  };

  const refreshAccessToken = async () => {
    const refresh = getRefreshToken();
    if (!refresh) throw new Error('No refresh token found');

    const res = await fetch(`${UGX_ASSET_MASTER_API_URL}/token/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh }),
    });

    if (!res.ok) throw new Error('Token refresh failed');

    const data = await res.json();
    localStorage.setItem('ugxAccess', data.access);
    return data.access;
  };

  const getData = async (endPoint, dType) => {
    setError('');
    let accessToken = getAccessToken();

    if (!accessToken) {
      try {
        accessToken = await login();
      } catch (err) {
        setError('Login failed: ' + err.message);
        return;
      }
    }

    let res = await fetch(`${UGX_ASSET_MASTER_API_URL}/${endPoint}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (res.status === 401) {
      try {
        accessToken = await refreshAccessToken();
        res = await fetch(`${UGX_ASSET_MASTER_API_URL}/${endPoint}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
      } catch (err) {
        try {
          accessToken = await login();
          res = await fetch(`${UGX_ASSET_MASTER_API_URL}/${endPoint}`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
        } catch (finalErr) {
          setError('Failed to login or refresh token: ' + finalErr.message);
          return;
        }
      }
    }

    if (!res.ok) {
      setError('Protected API call failed: ' + res.status);
      return;
    }

    const data = await res.json();

    switch (dType) {
      case 'type':
        setTypeData(data?.results || []);
        break;
      case 'make':
        setMakeData(data?.asset_makes || []);
        break;
      case 'model':
        setModelData(data?.asset_models || []);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (open) {
      getData('assets/asset-type/', 'type');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const getAssetMakes = (id) => {
    getData(`assets/asset-type/${id}/?page=1`, 'make');
  };

  const getAssetModels = (id) => {
    getData(`assets/asset-make/${id}/?page=1`, 'model');
  };

  // Reset form when modal closes
  const handleClose = () => {
    setSelectedType('');
    setSelectedMake('');
    setSelectedModel('');
    setMakeData([]);
    setModelData([]);
    setError('');
    onClose();
  };

  return (
    <GlobalModal
      isOpen={open}
      onClose={handleClose}
      title="Add Parameters"
      size="lg"
      footer={
        <>
          <Button
            colorScheme="red"
            variant="outline"
            mr={3}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={() => getModelParameters(selectedModel)}
            isDisabled={!selectedModel}
          >
            Add
          </Button>
        </>
      }
      isCentered
    >
      <FormControl mb={4}>
        <FormLabel>Asset Type</FormLabel>
        <InterfaceSelect
          placeholder="Select Asset Type"
          value={selectedType}
          options={Object.fromEntries(
            typeData.map((type) => [type.name, type.id]),
          )}
          onChange={(selected) => {
            setSelectedType(selected);
            getAssetMakes(selected);
          }}
        />
      </FormControl>

      <FormControl mb={4}>
        <FormLabel>Asset Make</FormLabel>
        <InterfaceSelect
          placeholder="Select Asset Make"
          value={selectedMake}
          options={Object.fromEntries(
            makeData.map((make) => [make.name, make.id]),
          )}
          onChange={(selected) => {
            setSelectedMake(selected);
            getAssetModels(selected);
          }}
        />
      </FormControl>

      <FormControl mb={4}>
        <FormLabel>Asset Model</FormLabel>
        <InterfaceSelect
          placeholder="Select Asset Model"
          value={selectedModel}
          options={Object.fromEntries(
            modelData.map((model) => [model.name, model.id]),
          )}
          onChange={(selected) => {
            setSelectedModel(selected);
          }}
        />
      </FormControl>

      {error && (
        <Text color="red.500" mt={2}>
          {error}
        </Text>
      )}
    </GlobalModal>
  );
}

export default AssetParamDialog;
